<template>
  <div class="w-full flex flex-col items-center gap-6">
    <!-- 上方卡片区域 -->
    <div
      class="relative flex justify-center items-center overflow-hidden select-none"
      :style="containerStyle"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        :ref="(el) => setCardRef(el, index)"
        class="absolute transition-all duration-500 ease-out cursor-grab active:cursor-grabbing"
        :style="getItemStyle(index, calculatedCardSpacing, 1 - sideScale, 1)"
        @mousedown="onMouseDown"
        @touchstart="onTouchStart"
        @click.capture="handleCardClick(index, $event)"
      >
        <slot name="card" :item="item" :index="index" :is-active="activeIndex === index">
          <!-- 默认卡片内容 -->
          <div
            :style="{
              width: cardWidth + 'px',
            }"
          >
            {{ index }}
          </div>
        </slot>
      </div>

      <!-- 导航按钮 -->
      <div class="absolute z-controls left-5">
        <Button color="glass" size="lg" :disabled="items.length <= 1" @click.stop="handlePrev">
          <Icon icon="Direction-Wireframe-Left2"></Icon>
        </Button>
      </div>

      <div class="absolute right-5 z-controls">
        <Button color="glass" size="lg" :disabled="items.length <= 1" @click.stop="handleNext">
          <Icon icon="Direction-Wireframe-Right2"></Icon>
        </Button>
      </div>
    </div>

    <!-- 下方图标区域 -->
    <div
      class="pl-5 relative w-full flex justify-center items-center overflow-hidden"
      :style="{ height: measuredIconHeight + 'px' }"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        :ref="(el) => setIconRef(el, index)"
        class="size-27 absolute cursor-pointer transition-all duration-500 ease-out"
        :style="getItemStyle(index, iconSpacing, 0, items.length, true)"
        @click="handleIconClick(index)"
      >
        <div
          class="w-full h-full flex flex-col items-center justify-center transition-colors duration-300 text-black-white"
        >
          <slot name="icon" :item="item" :index="index" :is-active="activeIndex === index">
            <!-- 默认图标内容 -->
            <div class="w-10 h-10 rounded-full flex items-center justify-center mb-2">
              Icon {{ index }}
            </div>
          </slot>
        </div>
      </div>

      <!-- 静态添加按钮 (如果存在 append-icon 插槽) -->
      <div
        v-if="$slots['append-icon']"
        class="size-27 absolute cursor-pointer transition-all duration-500 ease-out"
        :style="getAppendIconStyle(items.length, iconSpacing)"
      >
        <slot name="append-icon" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    toRefs,
    computed,
    ref,
    onMounted,
    onUpdated,
    type ComponentPublicInstance,
    useSlots,
    watch,
  } from 'vue'
  import { useCarousel3D } from './useCarousel3D'
  import { Icon, Button } from '@rapoo/element'

  const props = withDefaults(
    defineProps<{
      items: any[]
      cardWidth?: number
      cardSpacing?: number // 手动覆盖间距。如果不提供，将根据容器宽度自动计算以适应边缘。
      cardGap?: number // 如果提供且提供了 cardSpacing，间距计算方式为：(宽度 * (1+scale)/2) + 间隙
      sideScale?: number // 侧边卡片的缩放比例
      iconSpacing?: number
      initialIndex?: number
    }>(),
    {
      cardWidth: 600,
      sideScale: 0.75, // 默认侧边缩放为 0.75
      cardGap: 40, // 默认间隙
      iconSpacing: 100,
    },
  )
  const emit = defineEmits<{
    change: [item: any, index: number]
  }>()

  const { items, cardWidth, sideScale, cardGap, cardSpacing } = toRefs(props)
  const itemsLength = computed(() => items.value.length)
  const $slots = useSlots()

  // 自动测量高度
  const measuredCardHeight = ref(400) // 默认兜底
  const measuredIconHeight = ref(100) // 默认兜底
  const cardRefs = ref<HTMLElement[]>([])
  const iconRefs = ref<HTMLElement[]>([])

  const setCardRef = (el: Element | ComponentPublicInstance | null, index: number) => {
    if (el) cardRefs.value[index] = el as HTMLElement
  }

  const setIconRef = (el: Element | ComponentPublicInstance | null, index: number) => {
    if (el) iconRefs.value[index] = el as HTMLElement
  }

  const updateHeights = () => {
    // 尝试测量 DOM
    if (cardRefs.value.length > 0) {
      // 优先测量当前激活的卡片，因为它是可见的且未缩放（scale=1）
      // 注意：cardRefs 可能是稀疏数组或顺序不对应，但通常 v-for 索引对应数组索引
      // activeIndex 是归一化索引
      const el = cardRefs.value[activeIndex.value] || cardRefs.value[0]

      if (el) {
        // 尝试测量第一个子元素（通常是 slot 内容），因为它决定了实际内容高度
        const content = el.firstElementChild as HTMLElement
        measuredCardHeight.value = content.offsetHeight
      }
    }

    if (iconRefs.value.length > 0) {
      const el = iconRefs.value[activeIndex.value] || iconRefs.value[0]
      if (el) {
        const content = el.firstElementChild as HTMLElement
        if (content && content.offsetHeight > 0) {
          // 添加 40px 缓冲以防止下方被截断（通常由于阴影或 margin）
          measuredIconHeight.value = content.offsetHeight + 40
        } else if (el.offsetHeight > 0) {
          measuredIconHeight.value = el.offsetHeight + 40
        }
      }
    }
  }

  onMounted(() => {
    // 延迟一点以确保 slot 内容已渲染
    setTimeout(updateHeights, 0)
  })

  onUpdated(() => {
    updateHeights()
  })

  // 根据宽度、缩放比例和间隙计算实际卡片间距
  const calculatedCardSpacing = computed(() => {
    // 1. 如果提供了手动间距，直接使用
    if (cardSpacing?.value) return cardSpacing.value

    // 2. 使用固定的间隙计算逻辑（不再依赖容器宽度进行裁剪适配）
    const scale = sideScale.value
    return (cardWidth.value * (1 + scale)) / 2 + cardGap.value
  })

  // 容器样式
  const containerStyle = computed(() => {
    return {
      width: '100%',
      height: measuredCardHeight.value + 'px',
    }
  })

  const {
    activeIndex,
    goTo,
    prev,
    next,
    getItemStyle,
    setDragOffset,
    endDrag,
    getAppendIconStyle,
  } = useCarousel3D({
    itemsCount: itemsLength,
    // 初始值，将在 getItemStyle 调用中被覆盖
    spacing: calculatedCardSpacing.value,
    scaleRatio: 1 - props.sideScale,
    visibleCount: 2,
    initialIndex: props.initialIndex,
  })

  watch(
    () => props.initialIndex,
    (newIndex) => {
      if (newIndex !== undefined) {
        goTo(newIndex)
      }
    },
  )

  // 拖动逻辑
  const isDragging = ref(false)
  const hasDragged = ref(false)
  const startX = ref(0)

  type CarouselCardClickEvent = MouseEvent & {
    __carouselWasActive?: boolean
  }

  function emitChange(previousIndex: number) {
    const index = activeIndex.value

    if (index === previousIndex) return

    const item = items.value[index]
    if (item) emit('change', item, index)
  }

  function handlePrev() {
    const previousIndex = activeIndex.value
    prev()
    emitChange(previousIndex)
  }

  function handleNext() {
    const previousIndex = activeIndex.value
    next()
    emitChange(previousIndex)
  }

  function handleIconClick(index: number) {
    const previousIndex = activeIndex.value
    goTo(index)
    emitChange(previousIndex)
  }

  function onMouseDown(e: MouseEvent) {
    isDragging.value = true
    hasDragged.value = false
    startX.value = e.clientX
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  function onMouseMove(e: MouseEvent) {
    if (!isDragging.value) return
    const diff = e.clientX - startX.value
    if (Math.abs(diff) > 5) hasDragged.value = true
    setDragOffset(diff)
  }

  function onMouseUp() {
    const previousIndex = activeIndex.value
    isDragging.value = false
    endDrag(100) // 阈值 100px
    emitChange(previousIndex)
    document.removeEventListener('mousemove', onMouseMove)
    document.removeEventListener('mouseup', onMouseUp)
  }

  function handleCardClick(index: number, e: CarouselCardClickEvent) {
    const wasActive = index === activeIndex.value
    e.__carouselWasActive = wasActive && !hasDragged.value

    if (hasDragged.value) {
      e.preventDefault()
      hasDragged.value = false
      return
    }

    if (!wasActive) {
      e.preventDefault()
      const previousIndex = activeIndex.value
      goTo(index)
      emitChange(previousIndex)
    }
  }

  // 触摸支持
  function onTouchStart(e: TouchEvent) {
    isDragging.value = true
    hasDragged.value = false
    startX.value = e.touches[0].clientX
    document.addEventListener('touchmove', onTouchMove)
    document.addEventListener('touchend', onTouchEnd)
  }

  function onTouchMove(e: TouchEvent) {
    if (!isDragging.value) return
    const diff = e.touches[0].clientX - startX.value
    if (Math.abs(diff) > 5) hasDragged.value = true
    setDragOffset(diff)
  }

  function onTouchEnd() {
    const previousIndex = activeIndex.value
    isDragging.value = false
    endDrag(50) // 触摸模式下降低阈值
    emitChange(previousIndex)
    document.removeEventListener('touchmove', onTouchMove)
    document.removeEventListener('touchend', onTouchEnd)
  }
</script>

<style scoped>
  /* 可选：为容器添加透视以获得更好的 3D 效果 */
</style>
